import React, { useState, useEffect } from 'react';
import './App.css';
import Map from './components/Map';
import AddRouteForm from './components/AddRouteForm';
import AirportSearch from './components/AirportSearch';
import RouteList from './components/RouteList';
import RouteDiscussion from './components/RouteDiscussion';
import UsernameModal from './components/UsernameModal';
import { getBadges, getStats } from './utils/achievements';
import { getWishlist } from './utils/wishlist';
import { API_BASE } from './utils/api';
import { authHeaders, isAuthenticated, getUsername } from './utils/auth';
import FilterBar, { applyFilters } from './components/FilterBar';
import SuggestionModal from './components/SuggestionModal';

function App() {
  const [routes, setRoutes] = useState([]);
  const [airports, setAirports] = useState([]);
  const [filterAirport, setFilterAirport] = useState('');
  const [sortBy, setSortBy] = useState('votes');
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [discussionRoute, setDiscussionRoute] = useState(null);
  const [userVotes, setUserVotes] = useState({});
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({});
  const [showWishlistOnly, setShowWishlistOnly] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({ region: '', status: '', distance: '' });
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [currentUsername, setCurrentUsername] = useState(getUsername());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  // Fetch airports on mount
  useEffect(() => {
    fetch(`${API_BASE}/airports`)
      .then(res => res.json())
      .then(data => setAirports(data))
      .catch(err => console.error('Error fetching airports:', err));
  }, []);

  // Fetch routes on mount and when filters change
  useEffect(() => {
    fetchRoutes();
    // eslint-disable-next-line
  }, [filterAirport, sortBy]);

  // Update badges when page loads
  useEffect(() => {
    setBadges(getBadges());
    setStats(getStats());
  }, [routes]);

  // Show username modal if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      const timer = setTimeout(() => setShowUsernameModal(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Close sidebar on Escape key
  useEffect(() => {
    if (!sidebarOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [sidebarOpen]);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAirport) params.append('airport', filterAirport);
      if (sortBy) params.append('sort_by', sortBy);

      const response = await fetch(`${API_BASE}/routes?${params}`, {
        headers: authHeaders()
      });

      const data = await response.json();
      setRoutes(data.filter(r => r.status !== 'rejected'));

      // Extract user votes
      const votes = {};
      data.forEach(route => {
        if (route.user_vote) {
          votes[route.id] = route.user_vote;
        }
      });
      setUserVotes(votes);
    } catch (err) {
      console.error('Error fetching routes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRouteAdded = (newRoute) => {
    setRoutes(prevRoutes => [newRoute, ...prevRoutes]);
    setBadges(getBadges());
    setStats(getStats());
  };

  const handleVote = (updatedRoute) => {
    setRoutes(prevRoutes =>
      prevRoutes.map(route =>
        route.id === updatedRoute.id ? updatedRoute : route
      )
    );
  };

  const handleClearFilter = () => {
    setFilterAirport('');
    setShowWishlistOnly(false);
  };

  const handleRouteClick = (route) => {
    setSelectedRoute(route);
  };

  const handleDiscussClick = (route) => {
    setDiscussionRoute(route);
  };

  const handleCloseDiscussion = () => {
    setDiscussionRoute(null);
    fetchRoutes(); // Refresh to update comment counts
  };

  const handleUsernameChosen = (username) => {
    setShowUsernameModal(false);
    setCurrentUsername(username);
  };

  // Filter routes for display
  const displayRoutes = applyFilters(
    showWishlistOnly ? routes.filter((r) => getWishlist().includes(r.id)) : routes,
    advancedFilters
  );

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon">✈</div>
            <h1>AeroSuggest</h1>
          </div>
          <div className="stats">
            <div className="stat-item">
              <span className="stat-label">Routes:</span>
              <span className="stat-value">{routes.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Airports:</span>
              <span className="stat-value">{airports.length}</span>
            </div>
            {currentUsername && (
              <div className="stat-item">
                <span className="stat-label">Signed in as:</span>
                <span className="stat-value">{currentUsername}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Mobile drag handle + search bar */}
        <div className="mobile-sheet-handle" />
        <div className="mobile-search-bar">
          <AirportSearch
            airports={airports}
            value={filterAirport}
            onChange={(val) => setFilterAirport(val)}
            placeholder="Search by airport…"
          />
          {filterAirport && (
            <button className="btn btn-secondary mobile-clear-btn" onClick={handleClearFilter}>
              Clear
            </button>
          )}
        </div>
        <div className="sidebar-header">
          <h2>Explore Routes</h2>
          <p className="sidebar-subtitle">Suggest routes and vote on community ideas</p>
        </div>
        
        {/* User Badges */}
        {badges.length > 0 && (
          <div className="user-badges-section">
            <h3>Your Badges</h3>
            <div className="badges-grid">
              {badges.map((badge, idx) => (
                <div key={idx} className="badge-item" title={badge.desc}>
                  <span className="badge-icon">{badge.icon}</span>
                  <span className="badge-name">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        {(stats.routesCreated > 0 || stats.votesGiven > 0 || stats.commentsPosted > 0) && (
          <div className="user-stats-section">
            <div className="stat-row">
              <span>Routes Created:</span>
              <strong>{stats.routesCreated || 0}</strong>
            </div>
            <div className="stat-row">
              <span>Votes Given:</span>
              <strong>{stats.votesGiven || 0}</strong>
            </div>
            <div className="stat-row">
              <span>Comments:</span>
              <strong>{stats.commentsPosted || 0}</strong>
            </div>
          </div>
        )}
        
        <div className="sidebar-content">
          <AddRouteForm onRouteAdded={handleRouteAdded} />

          <div className="filter-section">
            <div className="form-group">
              <label htmlFor="sortBy">Sort By</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="votes">Most Voted</option>
                <option value="recent">Recently Added</option>
                <option value="distance">Shortest Distance</option>
                <option value="comments">Most Discussed</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="filter">Filter by Airport</label>
              <AirportSearch
                id="filter"
                airports={airports}
                value={filterAirport}
                onChange={(val) => setFilterAirport(val)}
                placeholder="Any airport..."
              />
              {filterAirport && (
                <button className="btn btn-secondary" style={{ marginTop: '0.5rem' }} onClick={handleClearFilter}>
                  Clear
                </button>
              )}
            </div>

            <FilterBar filters={advancedFilters} onChange={setAdvancedFilters} />

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showWishlistOnly}
                  onChange={(e) => setShowWishlistOnly(e.target.checked)}
                />
                <span>Show Wishlist Only ({getWishlist().length})</span>
              </label>
            </div>
          </div>

          <div className="route-list-section">
            <h3>Suggested Routes</h3>
            {loading ? (
              <div className="loading"></div>
            ) : (
              <RouteList 
                routes={displayRoutes}
                onVote={handleVote}
                onRouteClick={handleRouteClick}
                onDiscussClick={handleDiscussClick}
                userVotes={userVotes}
              />
            )}
          </div>
        </div>
      </aside>

      <Map 
        routes={displayRoutes}
        onRouteClick={handleRouteClick}
        selectedRoute={selectedRoute}
      />

      {/* Mobile FABs — hidden on desktop via CSS */}
      <div className={`fab-container${sidebarOpen ? ' fab-hidden' : ''}`}>
        <button
          className="fab fab-suggest"
          onClick={() => setShowSuggestionModal(true)}
          title="Suggest an improvement"
          aria-label="Suggest an improvement"
        >
          💡
        </button>
        <button
          className="fab fab-menu"
          onClick={() => setSidebarOpen(o => !o)}
          title={sidebarOpen ? 'Close explore panel' : 'Explore routes'}
          aria-label={sidebarOpen ? 'Close explore panel' : 'Explore routes'}
          aria-expanded={sidebarOpen}
        >
          ☰
        </button>
      </div>

      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {discussionRoute && (
        <RouteDiscussion
          route={discussionRoute}
          onClose={handleCloseDiscussion}
        />
      )}

      {showUsernameModal && (
        <UsernameModal onComplete={handleUsernameChosen} />
      )}

      {showSuggestionModal && (
        <SuggestionModal onClose={() => setShowSuggestionModal(false)} />
      )}
    </div>
  );
}

export default App;
